import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import { Box, CircularProgress, IconButton, Paper, Typography } from '@mui/material';
import { createGlobalStyle } from 'styled-components';
import {
  MARKETING_MAX_WIDTH,
  MOBILE_BRAND_BAR_CONTENT_HEIGHT_COMPACT_PX,
  MOBILE_BRAND_BAR_CONTENT_HEIGHT_PX,
  SITE_HEADER_CONTENT_HEIGHT_MOBILE_PX,
  SITE_HEADER_CONTENT_HEIGHT_PX,
} from '../../styles/navigation/HeaderStyles';
import { getToken, isTokenValid } from '../../utils/auth/tokenManager';
import {
  completeGoogleOneTapLogin,
  googleOneTapClearsDesktopHeader,
  googleOneTapReturnTo,
  googleOneTapUsesFedcm,
  googleOneTapUsesOwnedCard,
  loadGoogleIdentityScript,
} from '../../utils/auth/googleOneTap';
import { shouldHideSiteHeaderOnMobile } from '../../utils/navigation/bottomNavigationVisibility';

/** Under the header (10010) and its menu (10011), above page content. */
const GOOGLE_ONE_TAP_Z_INDEX = 10009;
/** Same cutoff as the mobile brand bar. */
const GOOGLE_ONE_TAP_MOBILE_QUERY = '(max-width: 800px)';
const GOOGLE_ONE_TAP_MOBILE_RADIUS_PX = 16;
/** Google's account card. Narrower screens use the space between the side insets. */
const GOOGLE_ONE_TAP_CARD_WIDTH_PX = 391;
/** Google's button iframe is not reliable below this width. */
const GOOGLE_ONE_TAP_BUTTON_MIN_PX = 200;
const GOOGLE_ONE_TAP_SAFE_TOP = 'env(safe-area-inset-top, 0px)';

function useGoogleOneTapMobileLayout() {
  const [mobileLayout, setMobileLayout] = useState(
    () => window.matchMedia(GOOGLE_ONE_TAP_MOBILE_QUERY).matches,
  );

  useEffect(() => {
    const media = window.matchMedia(GOOGLE_ONE_TAP_MOBILE_QUERY);
    const sync = () => setMobileLayout(media.matches);
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  return mobileLayout;
}

/** Login and signup already have their own Google button. */
const GOOGLE_ONE_TAP_HIDDEN_PATHS = new Set(['/login', '/sign-up', '/signup']);

function hasMilletSession() {
  return isTokenValid(getToken());
}

function isGoogleOneTapHiddenPath(pathname) {
  return GOOGLE_ONE_TAP_HIDDEN_PATHS.has(pathname);
}

function googleOneTapCardTopSx(hideSiteHeader) {
  if (hideSiteHeader) {
    return { top: GOOGLE_ONE_TAP_SAFE_TOP };
  }

  return {
    top: `calc(${SITE_HEADER_CONTENT_HEIGHT_MOBILE_PX}px + ${GOOGLE_ONE_TAP_SAFE_TOP})`,
    'body.has-mobile-brand-bar &': {
      top: `calc(${MOBILE_BRAND_BAR_CONTENT_HEIGHT_PX}px + ${GOOGLE_ONE_TAP_SAFE_TOP})`,
    },
    'body.has-mobile-brand-bar-compact &': {
      top: `calc(${MOBILE_BRAND_BAR_CONTENT_HEIGHT_COMPACT_PX}px + ${GOOGLE_ONE_TAP_SAFE_TOP})`,
    },
  };
}

/** Show Millet's button when Google refuses the automatic prompt. */
function googleOneTapPromptNeedsButton(notification) {
  if (typeof notification?.isNotDisplayed === 'function' && notification.isNotDisplayed()) {
    return true;
  }
  return typeof notification?.isSkippedMoment === 'function'
    && notification.isSkippedMoment()
    && notification.getSkippedReason?.() === 'issuing_failed';
}

function googleOneTapPromptIsDisplayed(notification) {
  return typeof notification?.isDisplayed === 'function' && notification.isDisplayed();
}

/** The visitor dismissed the prompt, or Google skipped it for a reason other than a failed issue. */
function googleOneTapPromptEnded(notification) {
  const skipped = typeof notification?.isSkippedMoment === 'function' && notification.isSkippedMoment();
  const dismissed = typeof notification?.isDismissedMoment === 'function' && notification.isDismissedMoment();
  return skipped || dismissed;
}

function googleOneTapCardPlacementSx(hideSiteHeader, userAgent, mobileLayout) {
  if (googleOneTapClearsDesktopHeader(userAgent, mobileLayout)) {
    return {
      top: `${SITE_HEADER_CONTENT_HEIGHT_PX}px`,
      right: `max(12px, calc((100vw - ${MARKETING_MAX_WIDTH}px) / 2))`,
      width: GOOGLE_ONE_TAP_CARD_WIDTH_PX,
    };
  }

  return {
    ...googleOneTapCardTopSx(hideSiteHeader),
    left: '12px',
    right: '12px',
    width: 'auto',
    maxWidth: GOOGLE_ONE_TAP_CARD_WIDTH_PX,
    mx: 'auto',
  };
}

function GoogleOneTapCard({
  busy,
  hideSiteHeader,
  mobileLayout,
  onDismiss,
  renderButtonNow,
  renderGeneration,
  userAgent,
}) {
  const buttonRef = useRef(null);
  const [compactPadding, setCompactPadding] = useState(false);
  const [buttonPainted, setButtonPainted] = useState(false);
  const showSpinner = busy || !buttonPainted;
  const buttonVisibility = showSpinner ? 'hidden' : 'visible';
  const cardPaddingX = compactPadding ? 0 : 1.5;

  useLayoutEffect(() => {
    if (!renderButtonNow) {
      setButtonPainted(false);
      return undefined;
    }

    const node = buttonRef.current;
    const renderButton = window.google?.accounts?.id?.renderButton;
    if (!node || typeof renderButton !== 'function') return undefined;

    const measured = Math.floor(node.clientWidth);
    if (measured < GOOGLE_ONE_TAP_BUTTON_MIN_PX && !compactPadding) {
      setCompactPadding(true);
      return undefined;
    }

    const width = measured >= GOOGLE_ONE_TAP_BUTTON_MIN_PX
      ? measured
      : GOOGLE_ONE_TAP_BUTTON_MIN_PX;
    node.replaceChildren();
    renderButton(node, {
      shape: 'pill',
      width,
    });

    const iframe = node.querySelector('iframe');
    if (!iframe) {
      setButtonPainted(true);
      return undefined;
    }

    let cancelled = false;
    const reveal = () => {
      if (!cancelled) setButtonPainted(true);
    };
    iframe.addEventListener('load', reveal, { once: true });
    return () => {
      cancelled = true;
      iframe.removeEventListener('load', reveal);
    };
  }, [compactPadding, renderButtonNow, renderGeneration]);

  return (
    <Paper
      role="dialog"
      aria-label="Sign in with Google"
      aria-busy={showSpinner}
      elevation={8}
      sx={{
        position: 'fixed',
        ...googleOneTapCardPlacementSx(hideSiteHeader, userAgent, mobileLayout),
        zIndex: GOOGLE_ONE_TAP_Z_INDEX,
        borderRadius: `${GOOGLE_ONE_TAP_MOBILE_RADIUS_PX}px`,
        border: 1,
        borderColor: 'border.subtle',
        px: cardPaddingX,
        py: 1.25,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Box sx={{ position: 'relative', flex: '1 1 auto', minWidth: 0, minHeight: 40 }}>
        <Box
          ref={buttonRef}
          sx={{ flex: '1 1 auto', minWidth: 0, minHeight: 40, visibility: buttonVisibility }}
        />
        {showSpinner ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <CircularProgress size={20} sx={{ color: 'text.secondary' }} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Loading
            </Typography>
          </Box>
        ) : null}
      </Box>
      <IconButton
        aria-label="Dismiss sign-in"
        onClick={onDismiss}
        size="small"
        sx={{ flex: '0 0 auto' }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Paper>
  );
}

const GoogleOneTapFrameStyle = createGlobalStyle`
  #credential_picker_container,
  #credential_picker_iframe {
    z-index: ${GOOGLE_ONE_TAP_Z_INDEX} !important;
  }

  #credential_picker_container {
    top: ${SITE_HEADER_CONTENT_HEIGHT_PX}px !important;
    right: max(12px, calc((100vw - ${MARKETING_MAX_WIDTH}px) / 2)) !important;
  }
`;

export default function GoogleOneTap() {
  const location = useLocation();
  const navigate = useNavigate();
  const exchangingRef = useRef(false);
  const locationRef = useRef(location);
  const navigateRef = useRef(navigate);
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
  const [isLoggedIn, setIsLoggedIn] = useState(hasMilletSession);
  const { userAgent } = window.navigator;
  const mobileLayout = useGoogleOneTapMobileLayout();
  const usesFedcm = googleOneTapUsesFedcm(userAgent);
  const showOwnedCard = googleOneTapUsesOwnedCard(userAgent, mobileLayout);
  const [shellClosed, setShellClosed] = useState(false);
  const [renderButtonNow, setRenderButtonNow] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [renderGeneration, setRenderGeneration] = useState(0);
  const dismissedRef = useRef(false);
  const shellClosedRef = useRef(false);
  const openedForSignInRef = useRef(false);
  const showPrompt = Boolean(clientId)
    && !isLoggedIn
    && !isGoogleOneTapHiddenPath(location.pathname);
  const hideSiteHeader = shouldHideSiteHeaderOnMobile(location.pathname);
  locationRef.current = location;
  navigateRef.current = navigate;
  shellClosedRef.current = shellClosed;

  useEffect(() => {
    const syncSession = () => {
      setIsLoggedIn(hasMilletSession());
    };
    const handleStorage = (event) => {
      if (event.key === 'token' || event.key === null) {
        syncSession();
      }
    };

    window.addEventListener('userLoggedIn', syncSession);
    window.addEventListener('userLoggedOut', syncSession);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('userLoggedIn', syncSession);
      window.removeEventListener('userLoggedOut', syncSession);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  useEffect(() => {
    if (!showPrompt) {
      setShellClosed(true);
      setRenderButtonNow(false);
      setSigningIn(false);
      window.google?.accounts?.id?.cancel();
      return undefined;
    }

    let cancelled = false;
    setShellClosed(dismissedRef.current);
    setRenderButtonNow(false);
    setSigningIn(false);

    const hideShell = () => {
      if (cancelled || exchangingRef.current) return;
      shellClosedRef.current = true;
      setRenderButtonNow(false);
      setShellClosed(true);
    };

    const showFallbackButton = () => {
      if (cancelled || exchangingRef.current || dismissedRef.current) {
        if (!cancelled && !exchangingRef.current) setShellClosed(true);
        return;
      }
      setShellClosed(false);
      setRenderButtonNow(true);
      setRenderGeneration((generation) => generation + 1);
    };

    const handleCredential = (response) => {
      const credential = response?.credential;
      if (!credential || exchangingRef.current) return;
      exchangingRef.current = true;
      openedForSignInRef.current = shellClosedRef.current;
      shellClosedRef.current = false;
      setSigningIn(true);
      setShellClosed(false);
      const { pathname: returnPath, search } = locationRef.current;
      completeGoogleOneTapLogin({
        credential,
        navigate: navigateRef.current,
        returnTo: googleOneTapReturnTo({
          pathname: returnPath,
          search,
        }),
      }).then((signedIn) => {
        if (signedIn) {
          window.google?.accounts?.id?.cancel();
          return;
        }
        if (cancelled) return;
        setSigningIn(false);
        const shellOpenedForExchange = openedForSignInRef.current;
        // showFallbackButton ignores calls while an exchange is still marked in progress.
        exchangingRef.current = false;
        openedForSignInRef.current = false;
        if (shellOpenedForExchange || dismissedRef.current) {
          shellClosedRef.current = true;
          setShellClosed(true);
          return;
        }
        showFallbackButton();
      }).finally(() => {
        exchangingRef.current = false;
        openedForSignInRef.current = false;
      });
    };

    loadGoogleIdentityScript()
      .then((googleId) => {
        if (cancelled || !googleId) return;
        if (dismissedRef.current) {
          googleId.cancel();
          return;
        }
        const config = {
          client_id: clientId,
          callback: handleCredential,
          auto_select: false,
          context: 'signin',
          cancel_on_tap_outside: true,
          use_fedcm_for_prompt: usesFedcm,
          itp_support: true,
        };
        if (!usesFedcm) config.ui_mode = 'card';
        googleId.initialize(config);
        if (showOwnedCard) {
          showFallbackButton();
          return;
        }

        googleId.prompt((notification) => {
          if (cancelled || exchangingRef.current) return;
          if (googleOneTapPromptNeedsButton(notification)) {
            showFallbackButton();
            return;
          }
          if (googleOneTapPromptIsDisplayed(notification) || googleOneTapPromptEnded(notification)) {
            hideShell();
          }
        });
      })
      .catch(() => {
        if (!cancelled) hideShell();
      });

    return () => {
      cancelled = true;
      window.google?.accounts?.id?.cancel();
    };
  }, [clientId, showOwnedCard, showPrompt, usesFedcm]);

  if (!showPrompt) return null;

  const dismissCard = () => {
    dismissedRef.current = true;
    setRenderButtonNow(false);
    setSigningIn(false);
    setShellClosed(true);
    window.google?.accounts?.id?.cancel();
  };

  return (
    <>
      <GoogleOneTapFrameStyle />
      {shellClosed ? null : (
        <GoogleOneTapCard
          busy={signingIn}
          hideSiteHeader={hideSiteHeader}
          mobileLayout={mobileLayout}
          onDismiss={dismissCard}
          renderButtonNow={renderButtonNow}
          renderGeneration={renderGeneration}
          userAgent={userAgent}
        />
      )}
    </>
  );
}
